import { FC, useEffect, useState } from 'react';
import { Grid, Typography, Divider } from '@mui/material';
import useCustomer from '../hooks/useCustomer';
import { useNavigate } from 'react-router-dom';
import useOrder from 'src/hooks/useOrder';
import DropdownButton from './controls/DropdownButton';
import AddBoxIcon from '@mui/icons-material/AddBox';
import ConfirmationDialog from './controls/ConfirmationDialog';
import WarningIcon from '@mui/icons-material/Warning';
import useOrderSteps from 'src/hooks/useOrderSteps';
import { Program } from 'src/types/program';
import TypeAhead from './controls/TypeAhead';
import api from '../api/Api';
import { isNullOrEmpty } from 'src/utils/helperFunctions';

interface PageHeaderProps {
  displayOrderButton?: boolean;
  rightSideContent?: React.ReactNode;
  headerTitle?: string;
  isFullScreen?: boolean;
  emitToggleFullScreen?: () => void;
  removeBottomMargin?: boolean;
  hasDivider?: boolean;
}

const PageHeader: FC<React.PropsWithChildren<PageHeaderProps>> = ({ displayOrderButton, rightSideContent, headerTitle, removeBottomMargin, hasDivider = false }) => {
  const { customer } = useCustomer();
  const { startNewOrder, order } = useOrder();
  let navigate = useNavigate();
  const { setCurrentOrderStep } = useOrderSteps();
  const [noSalesAllowed, showNoSalesAllowed] = useState<boolean>(false);
  const [orderOptions, setOrderOptions] = useState<string[]>(['Warehouse Order', 'Direct Order', 'Plant Order']);
  const [showPromoConfirmation, setShowPromoConfirmation] = useState(false);
  const [activePromotions, setActivePromotions] = useState<Program[]>([]);
  const [filteredPromotions, setFilteredPromotions] = useState<Program[]>([]);
  const [selectedPromotion, setSelectedPromotion] = useState<Program>(null);

  useEffect(() => {
    getActivePromotions();
  }, []);

  const newOrderClicked = async (value: number) => {
    try {
      const termCode = !isNullOrEmpty(selectedPromotion?.earlyOrderTermCode)
        ? selectedPromotion?.earlyOrderTermCode
        : !isNullOrEmpty(customer.selectedShipToTermCode)
          ? customer.selectedShipToTermCode
          : !isNullOrEmpty(customer.defaultShipToTermCode)
            ? customer.defaultShipToTermCode
            : customer.defaultTermCode;

      let sfaOrderId = await startNewOrder(customer.number, value, termCode, selectedPromotion?.programId);

      // Check the customer's sales order status
      if ((customer?.salesOrderStatus?.toUpperCase() ?? '') === 'N') {
        showNoSalesAllowed(true);
      } else {
        setCurrentOrderStep(0);
        navigate(`/${customer.number}/OrderEntry/${sfaOrderId}`);
      }
    } catch (err) {
      console.error('Error starting new order', err);
      showNoSalesAllowed(false); // Reset any error UI if needed
    }
  };

  const getActivePromotions = () => {
    api
      .get<Program[]>('/show/active-programs')
      .then((res) => {
        if (res && res.length > 0) {
          setActivePromotions(res);
          setFilteredPromotions(res);
          setOrderOptions((prevOptions) => {
            if (!prevOptions.includes('Vendor Promotion Order')) {
              return [...prevOptions, 'Vendor Promotion Order'];
            }
            return prevOptions;
          });
        }
      })
      .catch((error) => {
        console.error('Error fetching active programs:', error);
      });
  };

  const fetch = (filter: string, callback: (results: Program[]) => void) => {
    if (!activePromotions) return;

    const filteredList = filter ? activePromotions.filter((p) => p.description.toLowerCase().includes(filter.toLowerCase())) : activePromotions;

    callback(filteredList);
  };

  const onFilterChange = (filter: string) => {
    fetch(filter, (result) => {
      setFilteredPromotions(result);
    });
  };

  const onChange = (value: Program) => {
    setSelectedPromotion(value);
  };

  const handleDropdownChange = (value: number) => {
    if (value === 3) {
      setShowPromoConfirmation(true);
    } else {
      newOrderClicked(value);
    }
  };

  return (
    <>
      <Grid size={{ xs: 12 }} className="content-header" sx={removeBottomMargin ? { mb: 0 } : { mb: 2 }}>
        <div className="content-header__container">
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid>
              <Typography color="textSecondary" variant="overline">
                <b>{headerTitle ?? 'Dashboard'}</b>
              </Typography>
            </Grid>
            {displayOrderButton && (
              <Grid size={{ xs: 'auto' }}>
                <>
                  {(customer?.salesOrderStatus?.toUpperCase() ?? '') === 'N' && <b style={{ color: 'red', marginRight: 8 }}>No Sales Allowed</b>}
                  <DropdownButton icon={<AddBoxIcon />} onChange={(value) => handleDropdownChange(value)} options={orderOptions} />
                </>
              </Grid>
            )}
            {rightSideContent && <Grid size={{ xs: 'auto' }}>{rightSideContent}</Grid>}
          </Grid>
        </div>
        {hasDivider && <Divider sx={{ borderColor: '#ccc' }} />}
      </Grid>

      <ConfirmationDialog
        title={'No Sales Allowed'}
        titleIconStart={<WarningIcon />}
        okText="Ok"
        cancelHidden={true}
        maxWidth="sm"
        open={noSalesAllowed}
        onClose={(confirmed) => {
          showNoSalesAllowed(false);

          if (!confirmed) {
            return;
          }

          navigate(`/${customer.number}/OrderEntry/${order.sfaOrderId}`);
        }}
      >
        This customer is marked no sales allowed in SXe. You can create an order, but will not be able to submit it until the customer is re-enabled. Please contact the Credit
        Department.
      </ConfirmationDialog>

      {/* SFA Promotional Orders Confirmation */}
      <ConfirmationDialog
        title={'New Vendor Promotion Order'}
        okText="Ok"
        cancelText="Cancel"
        maxWidth="xs"
        open={showPromoConfirmation}
        okDisabled={!selectedPromotion}
        onClose={(confirmed) => {
          setShowPromoConfirmation(false);
          if (!confirmed) return;

          newOrderClicked(3);
        }}
      >
        <TypeAhead
          sx={{ width: '100%', margin: 0 }}
          id="programFilterInput"
          defaultValue={selectedPromotion}
          data={filteredPromotions || []}
          minFilterLength={1}
          onChange={onChange}
          onFilterChange={onFilterChange}
          getOptionLabel={(option) => option.description}
          getOptionKey={(option: Program) => option.programId}
          placeholder={'Filter by program...'}
        />
      </ConfirmationDialog>
    </>
  );
};

export default PageHeader;
